import { useSpring } from "@react-spring/three";
import { useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import { Color, PointLight, Vector3 } from "three";
import store, { cameraStore } from "../../../../appStore";

import { useObjectCallback, useSheet } from "../../../../hooks/useTheatre";
import { findObjectStartsWithKey } from "../../../../utils/FindObjects";
import DragSphere from "../../DragSphere";

import fragmentShaderReplacement from "../../shader/VertexColorHeat_Distance_replacement_frag.glsl";
import fragmentShaderAddition from "../../shader/VertexColorHeat_Distance_addition_frag.glsl";

import vertexShaderReplacement from "../../shader/VertexColorHeat_Distance_replacement_vert.glsl";
import vertexShaderAddition from "../../shader/VertexColorHeat_Distance_addition_vert.glsl";

const LightName = "Sinac_Light_asdfadsfadsf";

const SinacInteraction = ({ action, scene, ...props }) => {
  const sheet = useSheet("EFD", "SINAC_INTERACTION", "0");

  const [dragActive, setDragActive] = useState(true);

  const [heatSource1, heatSource2, heatSource3] = useMemo(() => {
    return [
      findObjectStartsWithKey("Heat_Target_1", scene),
      findObjectStartsWithKey("Heat_Target_2", scene),
      findObjectStartsWithKey("Heat_Target_3", scene),
    ];
  });

  const [workpiece1, workpiece2, workpiece3] = useMemo(() => {
    // var workpiece = findObjectStartsWithKey(
    //   "Weldac_Animation9_Weldac_Animation6_ME_Pipe",
    //   object3d
    // );
    // var workpiece = nodes.ME_HardeningTempering_Workpiece001;
    var workpiece1 = findObjectStartsWithKey("Workpiece_1", scene);
    var workpiece2 = findObjectStartsWithKey("Workpiece_2", scene);
    var workpiece3 = findObjectStartsWithKey("Workpiece_3", scene);

    // var heatSource = findObjectStartsWithKey("Heat_Source", object3d);
    // var heatSource = nodes.Heat_Source;

    // var heatSourceWorld = new Vector3();
    // heatSource1.getWorldPosition(heatSourceWorld);

    // var materialClone = workpiece1.material.clone();
    // materialClone.onBeforeCompile = (s) => {
    //   s.uniforms.uHeat = {
    //     value: 0.0,
    //   };
    //   s.uniforms.uHeatFallof = {
    //     value: 0.9,
    //   };
    //   s.uniforms.uHardened = {
    //     value: 1.0,
    //   };
    //   s.uniforms.uHeatSource = {
    //     value: heatSourceWorld.clone(),
    //   };
    //   s.uniforms.uHeatDistance = {
    //     value: 8,
    //   };
    //   s.vertexShader = `${vertexShaderAddition}${s.vertexShader}`;
    //   s.vertexShader = s.vertexShader.replace(
    //     `#include <fog_vertex>`,
    //     vertexShaderReplacement
    //   );

    //   s.fragmentShader = `${fragmentShaderAddition}\n${s.fragmentShader}`;
    //   s.fragmentShader = s.fragmentShader.replace(
    //     `#include <color_fragment>`,
    //     ``
    //   );
    //   s.fragmentShader = s.fragmentShader.replace(
    //     `#include <dithering_fragment>`,
    //     fragmentShaderReplacement
    //   );

    //   materialClone.userData.shader = s;

    //   // workpiece2.material = materialClone.clone();
    //   // heatSource2.getWorldPosition(heatSourceWorld);
    //   // workpiece2.material.userData.shader.uniforms.uHeatSource.value =
    //   //   heatSourceWorld.clone();

    //   workpiece3.material = materialClone.clone();
    //   heatSource3.getWorldPosition(heatSourceWorld);
    //   workpiece3.material.userData.shader.uniforms.uHeatSource.value =
    //     heatSourceWorld.clone();
    // };

    var materialClone = workpiece1.material.clone();
    materialClone.onBeforeCompile = (s) => {
      handleBeforeCompile(materialClone, s, heatSource1);
    };

    workpiece1.material = materialClone;

    var materialClone2 = workpiece2.material.clone();
    materialClone2.onBeforeCompile = (s) => {
      handleBeforeCompile(materialClone2, s, heatSource2);
    };
    workpiece2.material = materialClone2;

    var materialClone3 = workpiece3.material.clone();
    materialClone3.onBeforeCompile = (s) => {
      handleBeforeCompile(materialClone3, s, heatSource3);
    };
    workpiece3.material = materialClone3;

    return [workpiece1, workpiece2, workpiece3];
  }, []);

  const handleBeforeCompile = (mat, s, heatSource) => {
    var heatSourceVec = new Vector3();
    heatSource.getWorldPosition(heatSourceVec);

    s.uniforms.uHeat = {
      value: 0.0,
    };
    s.uniforms.uHeatFallof = {
      value: 0.9,
    };
    s.uniforms.uHardened = {
      value: 1.0,
    };
    s.uniforms.uHeatSource = {
      value: heatSourceVec,
    };
    s.uniforms.uHeatDistance = {
      value: 8,
    };
    s.vertexShader = `${vertexShaderAddition}${s.vertexShader}`;
    s.vertexShader = s.vertexShader.replace(`#include <fog_vertex>`, vertexShaderReplacement);

    s.fragmentShader = `${fragmentShaderAddition}\n${s.fragmentShader}`;
    s.fragmentShader = s.fragmentShader.replace(`#include <color_fragment>`, ``);
    s.fragmentShader = s.fragmentShader.replace(`#include <dithering_fragment>`, fragmentShaderReplacement);

    mat.userData.shader = s;
  };

  const Light = useMemo(() => {
    var l = findObjectStartsWithKey(LightName, scene);
    if (l) return l;

    var target = heatSource1;

    var light = new PointLight(new Color("#ff0000"), 0);

    // light.add(debugSphere);
    light.position.copy(target.position);
    light.name = LightName;
    light.distance = 0.5;
    light.decay = 1;
    target.add(light);
    light.position.copy(new Vector3());

    return light;
  }, []);

  const heatSourceWorldPosition = useMemo(() => {
    return new Vector3();
  }, []);

  const animationConfig = useMemo(() => {
    return {
      animationTime: 0,
      heatIntesity: 0,
      heatIntesity2: 0,
      heatIntesity3: 0,
      lightIntesity: 0,
    };
  }, []);

  useObjectCallback(
    sheet,
    "Animation",
    animationConfig,
    ({ animationTime, heatIntesity, heatIntesity2, heatIntesity3, lightIntesity }) => {
      action.time = sheet.sequence.position;

      if (Light) {
        Light.intensity = lightIntesity;
      }

      // heatSource1.getWorldPosition(heatSourceWorldPosition);

      if (workpiece1.material.userData.shader) {
        // workpiece.material.userData.shader.uniforms.uHeatSource.value =
        //   heatSourceWorldPosition;

        workpiece1.material.userData.shader.uniforms.uHeat.value = heatIntesity;
        // workpiece.material.userData.shader.uniforms.uHardened.value = hardened;
      }
      if (workpiece2.material.userData.shader) {
        workpiece2.material.userData.shader.uniforms.uHeat.value = heatIntesity2;
      }
      if (workpiece3.material.userData.shader) {
        workpiece3.material.userData.shader.uniforms.uHeat.value = heatIntesity3;
      }
    },
  );

  useEffect(() => {
    cameraStore.rotateWithMouse = false;

    action.reset().play().fadeIn(0.5);
    action.play();
    action.paused = true;
    sheet.sequence.position = 0;
    // sheet.sequence.play({ iterationCount: Infinity });
    return () => {
      cameraStore.rotateWithMouse = true;
      cameraStore.enabled = true;
      sheet.sequence.position = 0;
      sheet.sequence.pause();
      action.time = 0;
      action.fadeOut(0.5);
    };
  }, []);

  const dragPoints = useMemo(() => {
    let startPos = new Vector3();
    var startObj = findObjectStartsWithKey("Drag_Start", scene);
    startObj.getWorldPosition(startPos);

    let endPos = new Vector3();
    var endObj = findObjectStartsWithKey("Drag_End", scene);
    endObj.getWorldPosition(endPos);

    return [startPos, endPos];
  }, [scene]);

  const handleDrag = (v) => {
    sheet.sequence.position = v * 2.5;
  };

  useEffect(() => {
    if (!store.autoPlay) return;
    const timeout = setTimeout(() => {
      sheet.sequence.play({ iterationCount: 1 });
    }, 3000);

    return () => clearTimeout(timeout);
  });

  const handleDragComplete = () => {
    cameraStore.enabled = true;
    sheet.sequence.play({ iterationCount: 1 }).then(() => setDragActive(true));
    setDragActive(false);
  };

  if (store.autoPlay) return;

  return (
    <group>
      {dragActive && (
        <DragSphere
          lineStart={dragPoints[0]}
          lineEnd={dragPoints[1]}
          onDrag={handleDrag}
          onComplete={handleDragComplete}
          planeRotation={90}
          sphereRadius={0.035}
          id="sinac-sphere"
          // debug
        />
      )}
    </group>
  );
};

export default SinacInteraction;
